import { AuditLog } from "../models/service/audit-log.model";
import { FormattedAuditLog, MessageDetail } from "../models/ui/formatted-audit-message.model";
import { AuditLogParsingRegEx } from "../constants/constants";
import { EnumUtilities } from "./enum.utilities";

export class AuditLogFormatter {
    /**
     * Parses a raw AuditLog's `{detail}`/`[subDetail]` encoded action string into a FormattedAuditLog
     * ready for display.
     * @param log The raw audit log entry from the API
     * @returns A FormattedAuditLog with the title, parsed details, and sub-details split out
     */
    static format(log: AuditLog): FormattedAuditLog {
        const messageTitle = log.action.split('\n{')[0];
        const messageDetails: MessageDetail[] = [];

        const parts = log.action.split(AuditLogParsingRegEx.FORMAT_DETAIL_PATTERN());
        let currentDetail: MessageDetail | null = null;

        for (const part of parts) {
            if (part.startsWith('{') && part.endsWith('}')) {
                // This is a main detail - save previous and start new
                if (currentDetail) {
                    messageDetails.push(currentDetail);
                }

                let detail = part.substring(1, part.length - 1);
                detail = detail.replace(AuditLogParsingRegEx.REPLACE_WITH_ITALICS_PATTERN(), '<i>$1</i>');
                detail = this.spaceCompoundPascalCase(detail);

                currentDetail = {
                    detail: detail,
                    subDetails: []
                };
            } else if (part.startsWith('[') && part.endsWith(']')) {
                // This is a sub-detail for the current detail
                if (currentDetail) {
                    const subDetail = this.spaceCompoundPascalCase(part.substring(1, part.length - 1));
                    currentDetail.subDetails!.push(subDetail);
                }
            }
        }

        if (currentDetail) {
            messageDetails.push(currentDetail);
        }

        return {
            timestamp: log.actionTimestamp,
            messageTitle: messageTitle,
            messageDetails: messageDetails.length > 0 ? messageDetails : undefined,
            note: log.note,
            actingEntityName: log.actingEntityName,
            actingEntityId: log.actingEntityId
        };
    }

    /**
     * Converts compound PascalCase tokens (e.g. "AccountsReceivable") into
     * spaced title case ("Accounts Receivable"). Single words (e.g. "Revenue",
     * "Active") are left untouched.
     */
    private static spaceCompoundPascalCase(value: string): string {
        return value.replace(/\b(?:[A-Z][a-z]+){2,}\b/g, match => EnumUtilities.convertToLabel(match));
    }
}
